import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import { LoaderComponent } from "../../Common/LoaderComponent";
import HeaderCard from "../../Common/HeaderCard";

const ProductLedger = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [initialStock, setInitialStock] = useState(null);
  const [purchaseData, setPurchaseData] = useState([]);
  const [returnData, setReturnData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const { id } = useParams();
  const [validationError, setValidationError] = useState(null);
  const [isValidatingId, setIsValidatingId] = useState(false);

  const validateAndSetProductById = (productId, productsList) => {
    // Reset validation state
    setValidationError(null);
    setIsValidatingId(true);

    // Check if productId is provided and valid
    if (!productId) {
      setIsValidatingId(false);
      return false;
    }

    // Validate ID format (should be a positive integer)
    const numericId = parseInt(productId, 10);
    if (isNaN(numericId) || numericId <= 0 || !Number.isInteger(numericId)) {
      setValidationError({
        type: "invalid_format",
        message: `Invalid product ID format: "${productId}". Product ID must be a positive integer.`,
        providedId: productId,
      });
      setIsValidatingId(false);
      return false;
    }

    // Check if product exists in the products list
    const targetProduct = productsList.find((p) => p.id === numericId);

    if (!targetProduct) {
      setValidationError({
        type: "not_found",
        message: `Product with ID "${numericId}" not found in the system.`,
        providedId: numericId,
        availableIds: productsList.map((p) => p.id).sort((a, b) => a - b),
      });
      setIsValidatingId(false);
      return false;
    }

    // Valid product found - set it as selected and fetch its data
    setSelectedProduct(targetProduct);
    fetchLedgerData(targetProduct.id);
    setIsValidatingId(false);
    return true;
  };

  // Fetch all products for selection
  const fetchProducts = () => {
    setLoading(true);
    Http.get("/product-wise-stock")
      .then((res) => {
        if (res.data.status === "success") {
          setProducts(res.data.item);
          setFilteredProducts(res.data.item);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        setLoading(false);
      });
  };

  // Fetch ledger data for selected product
  const fetchLedgerData = (productId) => {
    setLedgerLoading(true);
    setPurchaseLoading(true);
    setReturnLoading(true);

    Http.get(`/product-ledger/${productId}`)
      .then((res) => {
        if (res.data.status === "success") {
          const transactions = res.data.transactions || [];
          setLedgerData(transactions);
          setSummaryData(res.data.summary || null);
          const prod = res.data.product || null;
          setInitialStock(
            prod && prod.initial_stock != null
              ? parseFloat(prod.initial_stock)
              : null,
          );

          // Separate purchase and return data
          const purchases = transactions.filter(
            (t) => t.transaction_type === "GRN" && t.movement_type === "IN",
          );
          const returns = transactions.filter(
            (t) =>
              t.transaction_type === "GRN_RETURN" && t.movement_type === "OUT",
          );

          setPurchaseData(purchases);
          setReturnData(returns);
        } else {
          setLedgerData([]);
          setSummaryData(null);
          setPurchaseData([]);
          setReturnData([]);
          setInitialStock(null);
        }
        setLedgerLoading(false);
        setPurchaseLoading(false);
        setReturnLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching ledger data:", err);
        setLedgerData([]);
        setSummaryData(null);
        setPurchaseData([]);
        setReturnData([]);
        setInitialStock(null);
        setLedgerLoading(false);
        setPurchaseLoading(false);
        setReturnLoading(false);
      });
  };

  useEffect(() => {
    if (products.length > 0) {
      // First priority: Check route parameter 'id'
      if (id) {
        const isValid = validateAndSetProductById(id, products);
        if (isValid) {
          return; // Exit early if route ID is valid
        }
      }

      // Second priority: Check URL search parameters
      const searchParams = new URLSearchParams(location.search);
      const productId = searchParams.get("productId");
      const productCode = searchParams.get("productCode");
      const productName = searchParams.get("productName");

      if (productId) {
        // Try to find the product by ID first
        let targetProduct = products.find((p) => p.id.toString() === productId);

        // If not found by ID, try to find by code
        if (!targetProduct && productCode) {
          targetProduct = products.find((p) => p.code === productCode);
        }

        // If not found by code, try to find by name
        if (!targetProduct && productName) {
          targetProduct = products.find(
            (p) => p.name === decodeURIComponent(productName),
          );
        }

        if (targetProduct) {
          setSelectedProduct(targetProduct);
          fetchLedgerData(targetProduct.id);
          // Set search term to highlight the selected product
          setSearchTerm("");
        }
      }
    }
  }, [products, location.search, id]);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle URL parameters for product selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const productId = searchParams.get("productId");
    const productCode = searchParams.get("productCode");
    const productName = searchParams.get("productName");

    if (productId && products.length > 0) {
      // Try to find the product by ID first
      let targetProduct = products.find((p) => p.id.toString() === productId);

      // If not found by ID, try to find by code
      if (!targetProduct && productCode) {
        targetProduct = products.find((p) => p.code === productCode);
      }

      // If not found by code, try to find by name
      if (!targetProduct && productName) {
        targetProduct = products.find(
          (p) => p.name === decodeURIComponent(productName),
        );
      }

      if (targetProduct) {
        setSelectedProduct(targetProduct);
        fetchLedgerData(targetProduct.id);
        // Set search term to highlight the selected product
        setSearchTerm("");
      }
    }
  }, [products, location.search]);

  // Handle product search
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.category_name &&
            product.category_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleProductSelect = (selectedOption) => {
    if (selectedOption) {
      const product = products.find((p) => p.id === selectedOption.value);
      if (product) {
        setSelectedProduct(product);
        fetchLedgerData(product.id);
      }
    } else {
      setSelectedProduct(null);
      setLedgerData([]);
      setSummaryData(null);
      setPurchaseData([]);
      setReturnData([]);
    }
  };

  // Convert products to react-select options
  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.code} - ${product.name}`,
    product: product,
  }));

  const getTransactionBadgeClass = (type) => {
    switch (type) {
      case "GRN":
        return "badge badge-success";
      case "Issue":
        return "badge badge-danger";
      case "Return":
        return "badge badge-warning";
      default:
        return "badge badge-info";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const makeTxKey = (t) =>
    `${t.id}:${t.transaction_type}:${t.movement_type}:${t.reference_no}`;

  const computeBalancesMap = (transactions, opening) => {
    // If opening not available, rely on API-provided running_balance
    if (opening === null || isNaN(opening)) {
      const map = new Map();
      transactions.forEach((t) => map.set(makeTxKey(t), t.running_balance));
      return map;
    }
    // Compute in chronological order, then map back to rendered order
    const sorted = [...transactions].sort((a, b) => {
      const da = new Date(a.transaction_date).getTime();
      const db = new Date(b.transaction_date).getTime();
      if (da !== db) return da - db;
      // tie-breaker for stable ordering when same date
      if (a.id !== b.id) return a.id - b.id;
      return String(a.reference_no).localeCompare(String(b.reference_no));
    });
    let bal = Number(opening);
    const map = new Map();
    for (const t of sorted) {
      const qty = parseFloat(t.quantity || 0);
      const delta =
        t.movement_type === "IN"
          ? Math.abs(qty)
          : t.movement_type === "OUT"
            ? -Math.abs(qty)
            : 0;
      bal += delta;
      map.set(makeTxKey(t), bal);
    }
    return map;
  };

  const computedBalancesMap = computeBalancesMap(ledgerData, initialStock);

  return (
    <div>
      <IsLogin />
      {/* {loading && <LoaderComponent />} */}

      <div className="am-mainpanel" style={{ marginTop: "50px" }}>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {/* {validationError && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="alert alert-danger" style={{
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fa fa-exclamation-triangle"></i>
                      </div>
                      <div>
                        <h6 className="mb-1" style={{ fontWeight: '600' }}>Product ID Validation Error</h6>
                        <p className="mb-2">{validationError.message}</p>
                        {validationError.type === 'not_found' && validationError.availableIds && (
                          <small style={{ opacity: 0.9 }}>
                            Available Product IDs: {validationError.availableIds.slice(0, 10).join(', ')}
                            {validationError.availableIds.length > 10 && ` ... and ${validationError.availableIds.length - 10} more`}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isValidatingId && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="alert alert-info" style={{
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <span>Validating Product ID: {id}...</span>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            {/* Product Selection Dropdown */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="form-group">
                  <label
                    htmlFor="productSelect"
                    className="form-label"
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      color: "#2c3e50",
                      marginBottom: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "0.8rem",
                      }}
                    >
                      <i className="fa fa-search"></i>
                    </div>
                    Select Product
                  </label>
                  <Select
                    id="productSelect"
                    options={productOptions}
                    value={
                      selectedProduct
                        ? productOptions.find(
                            (option) => option.value === selectedProduct.id,
                          )
                        : null
                    }
                    onChange={handleProductSelect}
                    placeholder="Search and select a product..."
                    isClearable
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "45px",
                        border: state.isFocused
                          ? "2px solid #667eea"
                          : "1px solid #e9ecef",
                        borderRadius: "8px",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.2rem rgba(102, 126, 234, 0.25)"
                          : "none",
                        zIndex: 9999999,
                        position: "relative",
                        "&:hover": {
                          borderColor: "#667eea",
                        },
                      }),
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999999,
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999999,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected
                          ? "#667eea"
                          : state.isFocused
                            ? "#f8f9fa"
                            : "white",
                        color: state.isSelected ? "white" : "#2c3e50",
                        padding: "12px 16px",
                        cursor: "pointer",
                        zIndex: 9999999,
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#6c757d",
                        fontSize: "14px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: "#2c3e50",
                        fontSize: "14px",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>

            {selectedProduct && summaryData && (
              <>
                <HeaderCard
                  icon="fa-book"
                  title="Product Stock Ledger of"
                  productName={selectedProduct.name}
                  subtitle="Track inventory movements and stock transactions"
                />
                <>
                  <style>
                    {`
                    .ledger-stats-container {
                      padding: 0.75rem 0;
                    }
                    
                    .ledger-stats-card {
                      background: linear-gradient(135deg, var(--card-bg-start), var(--card-bg-end));
                      border: none;
                      border-radius: 12px;
                      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
                      transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                      overflow: hidden;
                      position: relative;
                      backdrop-filter: blur(10px);
                      min-height: 100px;
                      margin-bottom: 1rem;
                    }
                    
                    .ledger-stats-card::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      height: 3px;
                      background: linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.8), rgba(255,255,255,0.3));
                      animation: shimmer 2s infinite;
                    }
                    
                    @keyframes shimmer {
                      0% { transform: translateX(-100%); }
                      100% { transform: translateX(100%); }
                    }
                    
                    .ledger-stats-card:hover {
                      transform: translateY(-4px) scale(1.01);
                      box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
                    }
                    
                    .ledger-stats-card.success {
                      --card-bg-start: #11998e;
                      --card-bg-end: #38ef7d;
                    }
                    
                    .ledger-stats-card.danger {
                      --card-bg-start: #fc466b;
                      --card-bg-end: #3f5efb;
                    }
                    
                    .ledger-stats-card.info {
                      --card-bg-start: #667eea;
                      --card-bg-end: #764ba2;
                    }
                    
                    .ledger-stats-card.primary {
                      --card-bg-start: #f093fb;
                      --card-bg-end: #f5576c;
                    }
                    
                    .ledger-card-content {
                      padding: 1rem;
                      display: flex;
                      align-items: center;
                      position: relative;
                      z-index: 2;
                      height: 100%;
                    }
                    
                    .ledger-icon-wrapper {
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 45px;
                      height: 45px;
                      background: rgba(255, 255, 255, 0.2);
                      border-radius: 50%;
                      backdrop-filter: blur(10px);
                      border: 2px solid rgba(255, 255, 255, 0.3);
                      transition: all 0.3s ease;
                      flex-shrink: 0;
                      margin-right: 1rem;
                    }
                    
                    .ledger-stats-card:hover .ledger-icon-wrapper {
                      transform: rotate(360deg) scale(1.05);
                      background: rgba(255, 255, 255, 0.3);
                    }
                    
                    .ledger-icon-wrapper i {
                      font-size: 1.2rem;
                      color: white;
                      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                    }
                    
                    .ledger-text-content {
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      text-align: left;
                    }
                    
                    .ledger-stats-number {
                      font-size: 1.6rem;
                      font-weight: 700;
                      color: white;
                      margin: 0 0 0.25rem 0;
                      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                      line-height: 1;
                      animation: countUp 0.8s ease-out;
                    }
                    
                    @keyframes countUp {
                      from { opacity: 0; transform: translateY(10px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                    
                    .ledger-stats-label {
                      font-size: 0.75rem;
                      font-weight: 500;
                      color: rgba(255, 255, 255, 0.9);
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                      margin: 0;
                      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                    }
                    
                    .ledger-stats-trend {
                      position: absolute;
                      top: 0.75rem;
                      right: 0.75rem;
                      background: rgba(255, 255, 255, 0.2);
                      border-radius: 12px;
                      padding: 0.2rem 0.5rem;
                      font-size: 0.65rem;
                      font-weight: 600;
                      color: white;
                      backdrop-filter: blur(5px);
                    }
                    
                    .ledger-floating-elements {
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      overflow: hidden;
                      pointer-events: none;
                    }
                    
                    .ledger-floating-elements::before,
                    .ledger-floating-elements::after {
                      content: '';
                      position: absolute;
                      width: 50px;
                      height: 50px;
                      border-radius: 50%;
                      background: rgba(255, 255, 255, 0.06);
                      animation: float 6s ease-in-out infinite;
                    }
                    
                    .ledger-floating-elements::before {
                      top: -25px;
                      right: -25px;
                      animation-delay: 0s;
                    }
                    
                    .ledger-floating-elements::after {
                      bottom: -25px;
                      left: -25px;
                      animation-delay: 3s;
                    }
                    
                    @keyframes float {
                      0%, 100% { transform: translateY(0px) rotate(0deg); }
                      50% { transform: translateY(-8px) rotate(180deg); }
                    }
                    
                    /* Laptop/Desktop optimized sizes */
                    @media (min-width: 992px) and (max-width: 1400px) {
                      .ledger-stats-card {
                        min-height: 95px;
                      }
                      .ledger-card-content {
                        padding: 0.9rem;
                      }
                      .ledger-icon-wrapper {
                        width: 42px;
                        height: 42px;
                        margin-right: 0.9rem;
                      }
                      .ledger-icon-wrapper i {
                        font-size: 1.1rem;
                      }
                      .ledger-stats-number {
                        font-size: 1.5rem;
                      }
                      .ledger-stats-label {
                        font-size: 0.7rem;
                      }
                      .ledger-stats-trend {
                        padding: 0.15rem 0.4rem;
                        font-size: 0.6rem;
                      }
                    }
                    
                    @media (max-width: 991px) {
                      .ledger-stats-card {
                        margin-bottom: 0.75rem;
                        min-height: 90px;
                      }
                      .ledger-card-content {
                        padding: 0.8rem;
                      }
                      .ledger-stats-number {
                        font-size: 1.4rem;
                      }
                      .ledger-icon-wrapper {
                        width: 40px;
                        height: 40px;
                        margin-right: 0.8rem;
                      }
                      .ledger-icon-wrapper i {
                        font-size: 1rem;
                      }
                      .ledger-stats-label {
                        font-size: 0.7rem;
                      }
                    }
                    
                    @media (max-width: 576px) {
                      .ledger-card-content {
                        flex-direction: column;
                        text-align: center;
                        padding: 0.75rem;
                      }
                      .ledger-icon-wrapper {
                        margin-right: 0;
                        margin-bottom: 0.5rem;
                        width: 35px;
                        height: 35px;
                      }
                      .ledger-icon-wrapper i {
                        font-size: 0.9rem;
                      }
                      .ledger-text-content {
                        text-align: center;
                      }
                      .ledger-stats-number {
                        font-size: 1.2rem;
                      }
                      .ledger-stats-label {
                        font-size: 0.65rem;
                      }
                    }
                  `}
                  </style>

                  <div className="ledger-stats-container mt-4">
                    <div className="row">
                      <div className="col-lg-3 col-md-6 col-sm-6">
                        <div className="card ledger-stats-card success text-white">
                          <div className="ledger-floating-elements"></div>
                          <div className="ledger-stats-trend">
                            <i className="fa fa-arrow-up"></i> Inbound
                          </div>
                          <div className="ledger-card-content">
                            <div className="ledger-icon-wrapper">
                              <i className="fa fa-arrow-down"></i>
                            </div>
                            <div className="ledger-text-content">
                              <div className="ledger-stats-number">
                                {summaryData.total_in || 0}
                              </div>
                              <div className="ledger-stats-label">Total In</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-6">
                        <div className="card ledger-stats-card danger text-white">
                          <div className="ledger-floating-elements"></div>
                          <div className="ledger-stats-trend">
                            <i className="fa fa-arrow-down"></i> Outbound
                          </div>
                          <div className="ledger-card-content">
                            <div className="ledger-icon-wrapper">
                              <i className="fa fa-arrow-up"></i>
                            </div>
                            <div className="ledger-text-content">
                              <div className="ledger-stats-number">
                                {summaryData.total_out || 0}
                              </div>
                              <div className="ledger-stats-label">
                                Total Out
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-6">
                        <div className="card ledger-stats-card info text-white">
                          <div className="ledger-floating-elements"></div>
                          <div className="ledger-stats-trend">
                            <i className="fa fa-list"></i> Activity
                          </div>
                          <div className="ledger-card-content">
                            <div className="ledger-icon-wrapper">
                              <i className="fa fa-exchange"></i>
                            </div>
                            <div className="ledger-text-content">
                              <div className="ledger-stats-number">
                                {summaryData.total_transactions || 0}
                              </div>
                              <div className="ledger-stats-label">
                                Transactions
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-6">
                        <div className="card ledger-stats-card primary text-white">
                          <div className="ledger-floating-elements"></div>
                          <div className="ledger-stats-trend">
                            <i className="fa fa-balance-scale"></i> Balance
                          </div>
                          <div className="ledger-card-content">
                            <div className="ledger-icon-wrapper">
                              <i className="fa fa-cubes"></i>
                            </div>
                            <div className="ledger-text-content">
                              <div className="ledger-stats-number">
                                {/* {(() => {
                                  if (computedBalancesMap && computedBalancesMap.size > 0) {
                                    const vals = Array.from(computedBalancesMap.values());
                                    return vals[vals.length - 1] || 0;
                                  }
                                  return summaryData.current_balance || 0;
                                })()} */}
                                {summaryData.current_balance || 0}
                              </div>
                              <div className="ledger-stats-label">
                                Current Balance
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              </>
            )}
            <div className="row">
              {/* Ledger Panel - Now Full Width */}
              <div className="col-12">
                {selectedProduct ? (
                  <div className="card modern-ledger-card">
                    <style>
                      {`
                        .modern-ledger-card {
                          background: linear-gradient(145deg, #ffffff, #f8f9fa);
                          border: none;
                          border-radius: 20px;
                          box-shadow: 
                            0 10px 30px rgba(0, 0, 0, 0.1),
                            0 1px 8px rgba(0, 0, 0, 0.06);
                          overflow: hidden;
                          position: relative;
                          transition: all 0.3s ease;
                        }
                        
                        .modern-ledger-card::before {
                          content: '';
                          position: absolute;
                          top: 0;
                          left: 0;
                          right: 0;
                          height: 4px;
                          background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
                          background-size: 300% 100%;
                          animation: gradientShift 3s ease infinite;
                        }
                        
                        @keyframes gradientShift {
                          0%, 100% { background-position: 0% 50%; }
                          50% { background-position: 100% 50%; }
                        }
                        
                        .modern-ledger-card:hover {
                          transform: translateY(-5px);
                          box-shadow: 
                            0 20px 40px rgba(0, 0, 0, 0.15),
                            0 5px 15px rgba(0, 0, 0, 0.08);
                        }
                        
                        .floating-particles {
                          position: absolute;
                          top: 0;
                          left: 0;
                          right: 0;
                          bottom: 0;
                          pointer-events: none;
                          overflow: hidden;
                        }
                        
                        .floating-particles::before,
                        .floating-particles::after {
                          content: '';
                          position: absolute;
                          width: 60px;
                          height: 60px;
                          border-radius: 50%;
                          background: linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
                          animation: floatParticles 8s ease-in-out infinite;
                        }
                        
                        .floating-particles::before {
                          top: 10%;
                          right: 10%;
                          animation-delay: 0s;
                        }
                        
                        .floating-particles::after {
                          bottom: 10%;
                          left: 10%;
                          animation-delay: 4s;
                        }
                        
                        @keyframes floatParticles {
                          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); opacity: 0.3; }
                          50% { transform: translateY(-20px) rotate(180deg) scale(1.1); opacity: 0.6; }
                        }
                      `}
                    </style>
                    <div className="floating-particles"></div>
                    {/* <div 
                      className="card-header text-white position-relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '0.375rem 0.375rem 0 0',
                        padding: '1rem',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                        margin: '0'
                      }}
                    >
                      <div 
                        className="position-absolute"
                        style={{
                          top: '-30%',
                          right: '-5%',
                          width: '120px',
                          height: '120px',
                          background: 'rgba(255, 255, 255, 0.08)',
                          borderRadius: '50%',
                          transform: 'rotate(45deg)'
                        }}
                      ></div>
                      
                      <div className="row align-items-center position-relative">
                        <div className="col">
                          <div className="d-flex align-items-center">
                            <div 
                              className="d-flex align-items-center justify-content-center mr-3"
                              style={{
                                width: '36px',
                                height: '36px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <i 
                                className="fa fa-chart-line" 
                                style={{ 
                                  fontSize: '14px',
                                  color: '#ffffff'
                                }}
                              ></i>
                            </div>
                            <div>
                              <h6 
                                className="mb-0 font-weight-bold"
                                style={{
                                  fontSize: '1.1rem',
                                  letterSpacing: '0.3px',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                Stock Ledger
                              </h6>
                              <small 
                                className="opacity-90 d-block"
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: '400',
                                  marginTop: '2px'
                                }}
                              >
                                {selectedProduct.name}
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="col-auto">
                          <div 
                            className="d-flex align-items-center px-3 py-1"
                            style={{
                              background: 'rgba(255, 255, 255, 0.25)',
                              borderRadius: '20px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            <div className="text-center">
                              <div 
                                className="font-weight-bold"
                                style={{
                                  fontSize: '0.95rem',
                                  lineHeight: '1.2'
                                }}
                              >
                                {formatCurrency(selectedProduct.stock_qty)}
                              </div>
                              <small 
                                className="opacity-90"
                                style={{
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}
                              >
                                {selectedProduct.unit_name} stock
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                    <div
                      className="card-body p-0"
                      style={{
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                        position: "relative",
                      }}
                    >
                      {ledgerLoading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading ledger data...</p>
                        </div>
                      ) : (
                        <div
                          className="table-responsive"
                          style={{
                            borderRadius: "0.5rem",
                            overflow: "hidden",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                          }}
                        >
                          <table
                            className="table mb-0"
                            style={{ backgroundColor: "#ffffff" }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  borderBottom: "none",
                                }}
                              >
                                <th
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-calendar mr-2"></i>Date
                                </th>
                                <th
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-tag mr-2"></i>Type
                                </th>
                                <th
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-hashtag mr-2"></i>
                                  Reference
                                </th>
                                <th
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-file-text mr-2"></i>
                                  Description
                                </th>
                                <th
                                  className="text-right"
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-arrow-up mr-2 text-success"></i>
                                  In Qty
                                </th>
                                <th
                                  className="text-right"
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-arrow-down mr-2 text-danger"></i>
                                  Out Qty
                                </th>
                                <th
                                  className="text-right"
                                  style={{
                                    color: "black",
                                    fontWeight: "600",
                                    fontSize: "0.85rem",
                                    padding: "1rem 0.75rem",
                                    borderBottom: "none",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  <i className="fa fa-balance-scale mr-2"></i>
                                  Balance
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {ledgerData.length > 0 ? (
                                <>
                                  {ledgerData.map((transaction, index) => (
                                    <tr
                                      key={transaction.id}
                                      style={{
                                        backgroundColor:
                                          index % 2 === 0
                                            ? "#ffffff"
                                            : "#f8f9fa",
                                        borderBottom: "1px solid #e9ecef",
                                        transition: "all 0.2s ease",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#e3f2fd";
                                        e.currentTarget.style.transform =
                                          "translateY(-1px)";
                                        e.currentTarget.style.boxShadow =
                                          "0 4px 12px rgba(0,0,0,0.1)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          index % 2 === 0
                                            ? "#ffffff"
                                            : "#f8f9fa";
                                        e.currentTarget.style.transform =
                                          "translateY(0)";
                                        e.currentTarget.style.boxShadow =
                                          "none";
                                      }}
                                    >
                                      <td
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <div
                                            className="mr-3"
                                            style={{
                                              width: "8px",
                                              height: "8px",
                                              borderRadius: "50%",
                                              backgroundColor: "#667eea",
                                            }}
                                          ></div>
                                          <div
                                            style={{
                                              fontSize: "0.9rem",
                                              fontWeight: "500",
                                              color: "#2c3e50",
                                            }}
                                          >
                                            {new Date(
                                              transaction.transaction_date,
                                            ).toLocaleDateString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                            })}
                                          </div>
                                        </div>
                                      </td>
                                      <td
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.4rem 0.8rem",
                                            borderRadius: "20px",
                                            fontSize: "0.75rem",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            background:
                                              transaction.transaction_type ===
                                              "Purchase"
                                                ? "linear-gradient(135deg, #28a745, #20c997)"
                                                : transaction.transaction_type ===
                                                    "Sale"
                                                  ? "linear-gradient(135deg, #dc3545, #fd7e14)"
                                                  : transaction.transaction_type ===
                                                      "Adjustment"
                                                    ? "linear-gradient(135deg, #ffc107, #fd7e14)"
                                                    : "linear-gradient(135deg, #6c757d, #495057)",
                                            color: "#ffffff",
                                            border: "none",
                                            boxShadow:
                                              "0 2px 8px rgba(0,0,0,0.15)",
                                          }}
                                        >
                                          <i
                                            className={`fa ${
                                              transaction.transaction_type ===
                                              "Purchase"
                                                ? "fa-shopping-cart"
                                                : transaction.transaction_type ===
                                                    "Sale"
                                                  ? "fa-shopping-bag"
                                                  : transaction.transaction_type ===
                                                      "Adjustment"
                                                    ? "fa-edit"
                                                    : "fa-exchange-alt"
                                            } mr-1`}
                                          ></i>
                                          {transaction.transaction_type}
                                        </span>
                                      </td>
                                      <td
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "0.9rem",
                                            fontWeight: "600",
                                            color: "#495057",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {transaction.reference_no}
                                        </div>
                                      </td>
                                      <td
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "0.85rem",
                                            color: "#6c757d",
                                            lineHeight: "1.4",
                                          }}
                                        >
                                          {transaction.description}
                                        </div>
                                      </td>
                                      <td
                                        className="text-right"
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        {transaction.movement_type === "IN" &&
                                          transaction.quantity > 0 && (
                                            <div
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.8rem",
                                                borderRadius: "15px",
                                                background:
                                                  "linear-gradient(135deg, #d4edda, #c3e6cb)",
                                                border: "1px solid #b8dacc",
                                                fontSize: "0.85rem",
                                                fontWeight: "600",
                                                color: "#155724",
                                              }}
                                            >
                                              <i className="fa fa-plus-circle mr-1"></i>
                                              {formatCurrency(
                                                Math.abs(transaction.quantity),
                                              )}
                                            </div>
                                          )}
                                      </td>
                                      <td
                                        className="text-right"
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        {transaction.movement_type === "OUT" &&
                                          transaction.quantity < 0 && (
                                            <div
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.8rem",
                                                borderRadius: "15px",
                                                background:
                                                  "linear-gradient(135deg, #f8d7da, #f1b0b7)",
                                                border: "1px solid #f1b0b7",
                                                fontSize: "0.85rem",
                                                fontWeight: "600",
                                                color: "#721c24",
                                              }}
                                            >
                                              <i className="fa fa-minus-circle mr-1"></i>
                                              {formatCurrency(
                                                Math.abs(transaction.quantity),
                                              )}
                                            </div>
                                          )}
                                      </td>
                                      <td
                                        className="text-right"
                                        style={{
                                          padding: "1rem 0.75rem",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.5rem 1rem",
                                            borderRadius: "20px",
                                            background:
                                              "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                                            border: "1px solid #90caf9",
                                            fontSize: "0.9rem",
                                            fontWeight: "700",
                                            color: "#0d47a1",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          <i className="fa fa-coins mr-2"></i>
                                          {formatCurrency(
                                            computedBalancesMap.get(
                                              `${transaction.id}:${transaction.transaction_type}:${transaction.movement_type}:${transaction.reference_no}`,
                                            ),
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                  {initialStock !== null &&
                                    !isNaN(initialStock) && (
                                      <tr
                                        style={{
                                          backgroundColor: "#fffbe6",
                                          borderBottom: "1px solid #e9ecef",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <div className="d-flex align-items-center">
                                            <div
                                              className="mr-3"
                                              style={{
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "#f7c948",
                                              }}
                                            ></div>
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "0.9rem",
                                                  fontWeight: "500",
                                                  color: "#2c3e50",
                                                }}
                                              >
                                                Opening
                                              </div>
                                              <small
                                                style={{
                                                  color: "#6c757d",
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                —
                                              </small>
                                            </div>
                                          </div>
                                        </td>
                                        <td
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <span
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              padding: "0.4rem 0.8rem",
                                              borderRadius: "20px",
                                              fontSize: "0.75rem",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              letterSpacing: "0.5px",
                                              background:
                                                "linear-gradient(135deg, #ffd966, #f7c948)",
                                              color: "#212529",
                                              border: "none",
                                              boxShadow:
                                                "0 2px 8px rgba(0,0,0,0.08)",
                                            }}
                                          >
                                            Opening
                                          </span>
                                        </td>
                                        <td
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "0.9rem",
                                              fontWeight: "600",
                                              color: "#495057",
                                              fontFamily: "monospace",
                                            }}
                                          >
                                            —
                                          </div>
                                        </td>
                                        <td
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "0.85rem",
                                              color: "#6c757d",
                                              lineHeight: "1.4",
                                            }}
                                          >
                                            Opening Stock
                                          </div>
                                        </td>
                                        <td
                                          className="text-right"
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          —
                                        </td>
                                        <td
                                          className="text-right"
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          —
                                        </td>
                                        <td
                                          className="text-right"
                                          style={{
                                            padding: "1rem 0.75rem",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              padding: "0.5rem 1rem",
                                              borderRadius: "20px",
                                              background:
                                                "linear-gradient(135deg, #fff3cd, #ffeeba)",
                                              border: "1px solid #ffe8a1",
                                              fontSize: "0.9rem",
                                              fontWeight: "700",
                                              color: "#856404",
                                              fontFamily: "monospace",
                                            }}
                                          >
                                            <i className="fa fa-coins mr-2"></i>
                                            {formatCurrency(initialStock)}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                </>
                              ) : initialStock !== null &&
                                !isNaN(initialStock) ? (
                                <tr
                                  style={{
                                    backgroundColor: "#fffbe6",
                                    borderBottom: "1px solid #e9ecef",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="mr-3"
                                        style={{
                                          width: "8px",
                                          height: "8px",
                                          borderRadius: "50%",
                                          backgroundColor: "#f7c948",
                                        }}
                                      ></div>
                                      <div>
                                        <div
                                          style={{
                                            fontSize: "0.9rem",
                                            fontWeight: "500",
                                            color: "#2c3e50",
                                          }}
                                        >
                                          Opening
                                        </div>
                                        <small
                                          style={{
                                            color: "#6c757d",
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          —
                                        </small>
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        padding: "0.4rem 0.8rem",
                                        borderRadius: "20px",
                                        fontSize: "0.75rem",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        background:
                                          "linear-gradient(135deg, #ffd966, #f7c948)",
                                        color: "#212529",
                                        border: "none",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                      }}
                                    >
                                      Opening
                                    </span>
                                  </td>
                                  <td
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.9rem",
                                        fontWeight: "600",
                                        color: "#495057",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      —
                                    </div>
                                  </td>
                                  <td
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.85rem",
                                        color: "#6c757d",
                                        lineHeight: "1.4",
                                      }}
                                    >
                                      Opening Stock
                                    </div>
                                  </td>
                                  <td
                                    className="text-right"
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    —
                                  </td>
                                  <td
                                    className="text-right"
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    —
                                  </td>
                                  <td
                                    className="text-right"
                                    style={{
                                      padding: "1rem 0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        padding: "0.5rem 1rem",
                                        borderRadius: "20px",
                                        background:
                                          "linear-gradient(135deg, #fff3cd, #ffeeba)",
                                        border: "1px solid #ffe8a1",
                                        fontSize: "0.9rem",
                                        fontWeight: "700",
                                        color: "#856404",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      <i className="fa fa-coins mr-2"></i>
                                      {formatCurrency(initialStock)}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <tr>
                                  <td
                                    colSpan="7"
                                    className="text-center"
                                    style={{ padding: "3rem 1rem" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          borderRadius: "50%",
                                          background:
                                            "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          marginBottom: "1rem",
                                          boxShadow:
                                            "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                      >
                                        <i
                                          className="fa fa-inbox"
                                          style={{
                                            fontSize: "2rem",
                                            color: "#6c757d",
                                          }}
                                        ></i>
                                      </div>
                                      <h6
                                        style={{
                                          color: "#495057",
                                          fontWeight: "600",
                                          marginBottom: "0.5rem",
                                        }}
                                      >
                                        No Transactions Found
                                      </h6>
                                      <p
                                        style={{
                                          color: "#6c757d",
                                          fontSize: "0.9rem",
                                          margin: "0",
                                        }}
                                      >
                                        No transaction history available for
                                        this product
                                      </p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center p-5">
                      <i
                        className="fa fa-arrow-up text-muted"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <h5 className="mt-3 text-muted">Select a Product</h5>
                      <p className="text-muted">
                        Choose a product from the dropdown above to view its
                        stock ledger
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Purchase and Return Details Section */}
            {selectedProduct &&
              (purchaseData.length > 0 || returnData.length) > 0 && (
                <>
                  {/* Purchase and Return Summary Cards */}
                  <div className="row mt-4">
                    <div className="col-md-3">
                      <div className="card bg-success text-white">
                        <div className="card-body text-center">
                          <h4>{purchaseData.length}</h4>
                          <p className="mb-0">Total Purchases</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-info text-white">
                        <div className="card-body text-center">
                          <h4>
                            {formatCurrency(
                              purchaseData.reduce(
                                (sum, p) => sum + Math.abs(p.total_amount || 0),
                                0,
                              ),
                            )}
                          </h4>
                          <p className="mb-0">Purchase Value</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-warning text-white">
                        <div className="card-body text-center">
                          <h4>{returnData.length}</h4>
                          <p className="mb-0">Total Returns</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-danger text-white">
                        <div className="card-body text-center">
                          <h4>
                            {formatCurrency(
                              returnData.reduce(
                                (sum, r) => sum + Math.abs(r.total_amount || 0),
                                0,
                              ),
                            )}
                          </h4>
                          <p className="mb-0">Return Value</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row mt-4">
                    {/* Purchase Details Table */}
                    <div className="col-lg-6">
                      <div className="card modern-ledger-card">
                        <div className="floating-particles"></div>
                        <div
                          className="card-header"
                          style={{
                            background:
                              "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                            border: "none",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                            padding: "1.5rem",
                            position: "relative",
                          }}
                        >
                          <h6
                            className="mb-0"
                            style={{
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                background:
                                  "linear-gradient(135deg, #28a745, #20c997)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "0.8rem",
                              }}
                            >
                              <i className="fa fa-shopping-cart"></i>
                            </div>
                            Purchase Details - {selectedProduct.name}
                          </h6>
                        </div>
                        <div
                          className="card-body p-0"
                          style={{
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                            position: "relative",
                          }}
                        >
                          {purchaseLoading ? (
                            <div className="text-center p-4">
                              <div
                                className="spinner-border text-success"
                                role="status"
                              >
                                <span className="sr-only">Loading...</span>
                              </div>
                              <p className="mt-2">Loading purchase data...</p>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-striped table-hover mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th>Date</th>
                                    <th>Vendor</th>
                                    <th>DP</th>
                                    <th>Reference</th>
                                    <th className="text-right">Quantity</th>
                                    <th className="text-right">Rate</th>
                                    <th className="text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {purchaseData.length > 0 ? (
                                    purchaseData.map((purchase) => (
                                      <tr key={purchase.id}>
                                        <td>
                                          <small className="text-muted">
                                            {new Date(
                                              purchase.transaction_date,
                                            ).toLocaleDateString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                            })}
                                          </small>
                                        </td>
                                        <td>
                                          <span className="font-weight-bold text-success">
                                            {purchase.vendor_name ||
                                              "Unknown Vendor"}
                                          </span>
                                        </td>
                                        <td>
                                          <small className="font-weight-bold">
                                            {
                                              purchase.direct_purchase_receipt_no
                                            }
                                          </small>
                                        </td>
                                        <td>
                                          <small className="font-weight-bold">
                                            {purchase.reference_no}
                                          </small>
                                        </td>
                                        <td className="text-right">
                                          <span className="text-success font-weight-bold">
                                            {formatCurrency(
                                              Math.abs(purchase.quantity),
                                            )}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span className="text-muted">
                                            {formatCurrency(
                                              purchase.unit_price || 0,
                                            )}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span className="font-weight-bold text-success">
                                            {formatCurrency(
                                              Math.abs(
                                                purchase.total_amount || 0,
                                              ),
                                            )}
                                          </span>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan="7"
                                        className="text-center p-4"
                                      >
                                        <i className="fa fa-info-circle text-muted mr-2"></i>
                                        No purchase records found for this
                                        product
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Return Details Table */}
                    <div className="col-lg-6">
                      <div className="card modern-ledger-card">
                        <div className="floating-particles"></div>
                        <div
                          className="card-header"
                          style={{
                            background:
                              "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                            border: "none",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                            padding: "1.5rem",
                            position: "relative",
                          }}
                        >
                          <h6
                            className="mb-0"
                            style={{
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                background:
                                  "linear-gradient(135deg, #ffc107, #fd7e14)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "0.8rem",
                              }}
                            >
                              <i className="fa fa-undo"></i>
                            </div>
                            Return Details - {selectedProduct.name}
                          </h6>
                        </div>
                        <div
                          className="card-body p-0"
                          style={{
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                            position: "relative",
                          }}
                        >
                          {returnLoading ? (
                            <div className="text-center p-4">
                              <div
                                className="spinner-border text-warning"
                                role="status"
                              >
                                <span className="sr-only">Loading...</span>
                              </div>
                              <p className="mt-2">Loading return data...</p>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-striped table-hover mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th>Date</th>
                                    <th>Vendor</th>
                                    <th>DP</th>
                                    <th>Reference</th>
                                    <th className="text-right">Quantity</th>
                                    <th className="text-right">Rate</th>
                                    <th className="text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {returnData.length > 0 ? (
                                    returnData.map((returnItem) => (
                                      <tr key={returnItem.id}>
                                        <td>
                                          <small className="text-muted">
                                            {new Date(
                                              returnItem.transaction_date,
                                            ).toLocaleDateString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                            })}
                                          </small>
                                        </td>
                                        <td>
                                          <span className="font-weight-bold text-warning">
                                            {returnItem.vendor_name ||
                                              "Unknown Vendor"}
                                          </span>
                                        </td>
                                        <td>
                                          <small className="font-weight-bold">
                                            {returnItem.direct_purchase_receipt_no ||
                                              "N/A"}
                                          </small>
                                        </td>
                                        <td>
                                          <small className="font-weight-bold">
                                            {returnItem.reference_no}
                                          </small>
                                        </td>
                                        <td className="text-right">
                                          <span className="text-danger font-weight-bold">
                                            {formatCurrency(
                                              Math.abs(returnItem.quantity),
                                            )}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span className="text-muted">
                                            {formatCurrency(
                                              returnItem.unit_price || 0,
                                            )}
                                          </span>
                                        </td>
                                        <td className="text-right">
                                          <span className="font-weight-bold text-danger">
                                            {formatCurrency(
                                              Math.abs(
                                                returnItem.total_amount || 0,
                                              ),
                                            )}
                                          </span>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan="7"
                                        className="text-center p-4"
                                      >
                                        <i className="fa fa-info-circle text-muted mr-2"></i>
                                        No return records found for this product
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLedger;
